import {
  AlpService,
  IAlpWithLessons,
  IIlpServiceState,
  ILesson,
  ILessonMetadata,
  AssignmentStatusEnum,
  ServiceStatusEnum,
  IAlp,
  IAssessment
} from "@imaginelearning/mps-api-service";
import { classMap } from "@imaginelearning/mps-ux-component-library";
/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useNavigate, useParams } from "react-router";
import useGradeRange from "../../hooks/usegraderange";
import EdgeBackButton from "../edge-back-button/edge-back-button";
import "./assignment-course-map.scss";
import AssignmentCourseMapHeader from "./assignment-coursemap-header";
import { AlpContext } from "./my-assignments-container";
import LessonCourseMap from "./course-map-components/lessons-course-map";
import AssessmentCourseMap from "./course-map-components/assessments-course-map";
import useSubject from "../../hooks/usesubject";
import useTheme from "../../hooks/usetheme";

const defaultALPState: IIlpServiceState<IAlpWithLessons> = {
  status: ServiceStatusEnum.LOADING,
  response: ""
};
const defaultILPMetadataState: IIlpServiceState<ILessonMetadata[]> = {
  status: ServiceStatusEnum.LOADING,
  response: []
};

function AssignmentCourseMap() {
  const { ilpID } = useParams();
  const [alp, setAlp] = useState<IIlpServiceState<IAlpWithLessons>>(defaultALPState);
  const [showSpinner, setShowSpinner] = useState(false);
  const [currentAlp, setCurrentAlp] = useState<IAlp | IAlpWithLessons>();
  const { theme } = useTheme();
  const { is6to12 } = useGradeRange();
  const navigate = useNavigate();
  const [lessonsCompleted, setLessonsCompleted] = useState(0);
  const { contentType, setContentType } = useContext(AlpContext);
  const { setSubject } = useSubject();

  const fetchAlp = async () => {
    setShowSpinner(true);
    const result: IIlpServiceState<IAlp> = await AlpService.getAlp(ilpID as string);
    setCurrentAlp(result.response as IAlp);
    if (typeof result.response !== "string") {
      setContentType(result.response.assignmentInfo.contentType);
      // getALP includes a subject - update the user.provider subject here
      setSubject(result.response.subject);
    }
    if (contentType === 1) await fetchAssessments();
    else if (contentType !== -1) await fetchLessons();
    setShowSpinner(false);
  };

  const [currentLessonMetadata, setCurrentLessonMetadata] =
    useState<IIlpServiceState<ILessonMetadata[]>>(defaultILPMetadataState);

  const fetchLessons = async () => {
    const result: IIlpServiceState<IAlpWithLessons> = await AlpService.getAlpIncludeAssignedLessons(ilpID as string);
    if (typeof result.response !== "string") {
      result.response.assignments = result.response.lessons;
      const completedLesson = result.response.assignments.filter(
        (assignmt: ILesson) =>
          assignmt.status === AssignmentStatusEnum.PASSED || assignmt.status === AssignmentStatusEnum.NOT_PASSED
      );
      setLessonsCompleted(completedLesson.length);

      const currentMetadata = await fetchCurrentMetaData({
        ...result.response,
        assignments: result.response.assignments
      });
      setCurrentLessonMetadata(currentMetadata);
      setAlp(result);
    }
  };
  const fetchAssessments = async () => {
    const result: IIlpServiceState<IAlp> = await AlpService.getAlpAssessments(ilpID as string);
    if (typeof result.response !== "string") {
      result.response.assignments = result.response.assignmentInfo.assessments ?? [];
      const completedLesson = result.response.assignments.filter(
        (assignmt: IAssessment) =>
          assignmt.status === AssignmentStatusEnum.PASSED || assignmt.status === AssignmentStatusEnum.NOT_PASSED
      );
      setLessonsCompleted(completedLesson.length);

      const currentMetadata = await fetchCurrentMetaData({
        ...result.response,
        assignments: []
      });
      setCurrentLessonMetadata(currentMetadata);
      setAlp(result as IIlpServiceState<IAlpWithLessons>);
    }
  };

  const fetchCurrentMetaData = async ({
    userId,
    standardCode,
    assignments
  }: {
    userId: string;
    standardCode: string;
    assignments: ILesson[];
  }) => {
    const studentId = userId;
    let metaData: IIlpServiceState<ILessonMetadata[]> = {
      ...defaultILPMetadataState,
      status: ServiceStatusEnum.LOADED
    };
    if (is6to12 && contentType === 0) {
      const currentLesson = assignments.find((lesson: ILesson) => lesson.isNext);
      if (currentLesson) {
        const { pathId } = currentLesson;
        metaData = await AlpService.getLessonMetadataByPathId(pathId, studentId, standardCode);
      }
    }

    return metaData;
  };

  useEffect(() => {
    if (ilpID) {
      setAlp(defaultALPState);
      fetchAlp();
    }
  }, [ilpID, contentType]);

  useEffect(() => {
    if (!ilpID) {
      navigate("/");
    }
  }, [ilpID, navigate]);

  // Start downloading doolittle saving on mastery check
  const getComponentByType = () => {
    if (typeof alp.response === "string") {
      return null;
    }

    switch (contentType) {
      case 0:
        return <LessonCourseMap alp={alp} currentLessonMetadata={currentLessonMetadata} />;
      case 1:
        return (
          <AssessmentCourseMap
            assignments={(alp.response.assignments as IAssessment[]) ?? []}
            alp={alp}
            fetchAlp={fetchAlp}
          />
        );
      default:
        return <LessonCourseMap alp={alp} currentLessonMetadata={currentLessonMetadata} />;
    }
  };

  if (showSpinner) {
    return (
      <div className="d-flex justify-content-center" style={{ height: "100vh", marginTop: "15%" }}>
        <Spinner variant="light" animation="border" role="status" style={{ height: "50px", width: "50px" }}>
          <span role="alert" className="visually-hidden">
            Loading
          </span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className={classMap("home-container w-100 h-100 abld-course-map-container", String(theme).replace(/_/g, "-"))}>
      <Container
        id="abld-assignment-lessons-container"
        className={classMap(
          String(theme).replace(/_/g, "-"),
          "abld-assignment-lessons-container",
          is6to12 ? "mw-100" : ""
        )}
      >
        {currentAlp && <AssignmentCourseMapHeader lessonsCompleted={lessonsCompleted} alp={currentAlp} />}
        <div className="lesson-list abld-lessons">
          <Row>
            <div className="me-auto ms-3 col-2 abld-assignment-back-button">
              <EdgeBackButton onBackClick={() => navigate(`/assignments/`)} />
            </div>
          </Row>
          {getComponentByType()}
        </div>
      </Container>
    </div>
  );
}

export default AssignmentCourseMap;
